import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IntegrationsApi, Permission } from '@up2stay/up2promo-api-client';
import {
  Badge,
  Button,
  cn,
  ghostAccentIconTileClass,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@up2stay/up2promo-ui';
import { Plug, Plus, Star } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { AppIonPage } from '@/core/components/app-ion-page';
import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { PageHeader } from '@/core/components/page-header';
import {
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsInfoHint } from '@/core/components/settings/settings-info-hint';
import { SettingsPageShell } from '@/core/components/settings/settings-page-shell';
import { SettingsSection } from '@/core/components/settings/settings-section';
import { usePermissions } from '@/core/hooks/use-permissions';
import { GOOGLE_REVIEW_PROVIDER } from '@/core/lib/review-channel-setup';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { integrationsQueryKeys } from '@/core/query-keys/integrations-query-keys';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { buildRedirectUrl } from '@/core/utils/redirect-url.utils';

const REVIEW_SOURCE_CATALOG = [
  {
    id: GOOGLE_REVIEW_PROVIDER,
    icon: Star,
  },
] as const;

function IntegrationsBreadcrumb({
  domainLabel,
  integrationsLabel,
}: {
  domainLabel: string;
  integrationsLabel: string;
}) {
  return (
    <>
      {domainLabel}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {integrationsLabel}
    </>
  );
}

export function WorkspaceIntegrationsPage() {
  const dict = useDictionary();
  const queryClient = useQueryClient();
  const currentWorkspace = useCurrentWorkspace();
  const { can } = usePermissions();
  /** The route only needs `integrations:read`; connecting needs manage. */
  const canManageIntegrations = can(Permission.INTEGRATIONS_MANAGE);
  const [catalogOpen, setCatalogOpen] = useState(false);

  const workspaceIntegrationsDict = dict.workspace.integrations;

  const { data: integrationsData } = useQuery({
    queryKey: integrationsQueryKeys.all(currentWorkspace?.id),
    queryFn: async () => {
      if (!currentWorkspace?.id) return null;
      const response = await IntegrationsApi.list({
        path: { workspaceId: currentWorkspace.id },
      });
      return response.data ?? null;
    },
    enabled: !!currentWorkspace?.id,
    staleTime: 60 * 1000,
  });

  const googleIntegration = integrationsData?.integrations?.find(
    (i) => i.provider === GOOGLE_REVIEW_PROVIDER,
  );
  const googleConnected = googleIntegration?.connected ?? false;

  const connectGoogleMutation = useMutation({
    mutationFn: async () => {
      if (!currentWorkspace?.id) return;
      const redirectUrl = buildRedirectUrl(
        shellPaths.moreWorkspaceIntegrations,
      );
      const state = `${currentWorkspace.id}|${encodeURIComponent(redirectUrl)}`;
      const response = await IntegrationsApi.getConnectUrl({
        path: { workspaceId: currentWorkspace.id },
        query: { state },
      });
      const url = (response as { data?: { url?: string } }).data?.url;
      if (url) window.location.href = url;
    },
  });

  const disconnectGoogleMutation = useMutation({
    mutationFn: async () => {
      if (!currentWorkspace?.id) return;
      await IntegrationsApi.disconnect({
        path: { workspaceId: currentWorkspace.id },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: integrationsQueryKeys.all(currentWorkspace?.id),
      });
    },
    onError: () => {
      toast.error(workspaceIntegrationsDict.google.errorDisconnect);
    },
  });

  const availableCatalog = REVIEW_SOURCE_CATALOG.filter(
    (item) => item.id === GOOGLE_REVIEW_PROVIDER && !googleConnected,
  );

  const breadcrumb = (
    <IntegrationsBreadcrumb
      domainLabel={dict.nav.navDomainWorkspaceAdmin}
      integrationsLabel={dict.nav.integrations}
    />
  );

  return (
    <AppIonPage>
      <AppPageContent>
        <AppPageLayout>
          <PageHeader
            breadcrumb={breadcrumb}
            title={workspaceIntegrationsDict.title}
          />
          <SettingsPageShell>
            {googleConnected ? (
              <SettingsSection
                id="integrations-connected"
                title={workspaceIntegrationsDict.connectedSection}
                grouped
              >
                <SettingsGroupRows>
                  <SettingsGroupRow>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                      <div className="flex min-w-0 items-center gap-3">
                        <div
                          className={cn(
                            'flex size-9 shrink-0 items-center justify-center rounded-[var(--r-control)]',
                            ghostAccentIconTileClass,
                          )}
                          aria-hidden
                        >
                          <Star className="size-4" />
                        </div>
                        <div className="flex min-w-0 items-center gap-0.5">
                          <p className="truncate text-[13.5px] font-semibold leading-tight text-foreground">
                            {workspaceIntegrationsDict.google.title}
                          </p>
                          <SettingsInfoHint
                            label={workspaceIntegrationsDict.google.title}
                          >
                            {workspaceIntegrationsDict.google.description}
                          </SettingsInfoHint>
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-wrap items-center gap-2">
                        <Badge variant="success">
                          {workspaceIntegrationsDict.google.connected}
                        </Badge>
                        {canManageIntegrations ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => disconnectGoogleMutation.mutate()}
                            disabled={disconnectGoogleMutation.isPending}
                          >
                            {workspaceIntegrationsDict.google.disconnect}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </SettingsGroupRow>
                </SettingsGroupRows>
              </SettingsSection>
            ) : (
              <SettingsSection
                id="integrations-catalog"
                title={workspaceIntegrationsDict.catalogTitle}
                footer={workspaceIntegrationsDict.catalogDescription}
                grouped
              >
                <SettingsGroupRows>
                  <SettingsGroupRow>
                    {canManageIntegrations ? (
                      <Button
                        variant="primary"
                        size="sm"
                        className="w-fit"
                        onClick={() => setCatalogOpen(true)}
                      >
                        <Plus className="size-4" />
                        {workspaceIntegrationsDict.addSource}
                      </Button>
                    ) : (
                      <p className="text-[13.5px] leading-relaxed text-muted-foreground">
                        {workspaceIntegrationsDict.catalogDescription}
                      </p>
                    )}
                  </SettingsGroupRow>
                </SettingsGroupRows>
              </SettingsSection>
            )}

            {canManageIntegrations &&
            googleConnected &&
            availableCatalog.length > 0 ? (
              <Button
                variant="dashed"
                size="sm"
                className="w-fit"
                onClick={() => setCatalogOpen(true)}
              >
                <Plus className="size-4" />
                {workspaceIntegrationsDict.addSource}
              </Button>
            ) : null}
          </SettingsPageShell>

          <ResponsiveDialog open={catalogOpen} onOpenChange={setCatalogOpen}>
            <ResponsiveDialogContent className="sm:max-w-md">
              <ResponsiveDialogHeader>
                <ResponsiveDialogTitle>
                  {workspaceIntegrationsDict.catalogTitle}
                </ResponsiveDialogTitle>
                <ResponsiveDialogDescription>
                  {workspaceIntegrationsDict.catalogDescription}
                </ResponsiveDialogDescription>
              </ResponsiveDialogHeader>
              <div className="flex flex-col gap-2 py-2">
                {availableCatalog.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className="flex items-center gap-3 rounded-[var(--r-control)] border border-border bg-card p-3 text-left shadow-[var(--shadow-card)] transition-colors hover:bg-muted/40"
                      onClick={() => {
                        if (item.id === GOOGLE_REVIEW_PROVIDER) {
                          connectGoogleMutation.mutate();
                        }
                      }}
                      disabled={connectGoogleMutation.isPending}
                    >
                      <div
                        className={cn(
                          'flex size-10 shrink-0 items-center justify-center rounded-[var(--r-control)]',
                          ghostAccentIconTileClass,
                        )}
                        aria-hidden
                      >
                        <Icon className="size-4" />
                      </div>
                      <div className="min-w-0 flex flex-col gap-0.5">
                        <p className="text-[13.5px] font-semibold leading-tight text-foreground">
                          {workspaceIntegrationsDict.google.title}
                        </p>
                        <p className="text-[13.5px] leading-relaxed text-muted-foreground">
                          {workspaceIntegrationsDict.google.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
                {availableCatalog.length === 0 ? (
                  <p className="flex items-center gap-2 text-[13.5px] leading-relaxed text-muted-foreground">
                    <Plug className="size-4 shrink-0" />
                    All available sources are connected.
                  </p>
                ) : null}
              </div>
            </ResponsiveDialogContent>
          </ResponsiveDialog>
        </AppPageLayout>
      </AppPageContent>
    </AppIonPage>
  );
}
